/**
 * Map a DOM Range to character offsets within verse body elements
 * ([data-verse-body]). Offsets match verse `body` from buildChapterVerseRows.
 */

function rangeIntersectsBody(range, body) {
  if (typeof range.intersectsNode === "function") {
    return range.intersectsNode(body);
  }
  const inner = document.createRange();
  inner.selectNodeContents(body);
  return (
    range.compareBoundaryPoints(Range.END_TO_START, inner) > 0 &&
    range.compareBoundaryPoints(Range.START_TO_END, inner) < 0
  );
}

/**
 * Single verse only — entire selection must lie in one [data-verse-body].
 * @param {Range} range
 * @returns {{ verseNum: number, startOffset: number, endOffset: number, bodyEl: HTMLElement } | null}
 */
export function getOffsetsInVerseBody(range) {
  if (!range || !range.commonAncestorContainer) return null;

  let startN = range.startContainer;
  let endN = range.endContainer;
  if (startN.nodeType === Node.TEXT_NODE) startN = startN.parentElement;
  if (endN.nodeType === Node.TEXT_NODE) endN = endN.parentElement;

  const body =
    startN?.closest?.("[data-verse-body]") || endN?.closest?.("[data-verse-body]");
  if (!body) return null;

  if (!body.contains(range.startContainer) || !body.contains(range.endContainer)) {
    return null;
  }

  const verseAttr = body.getAttribute("data-verse");
  const verseNum = verseAttr ? parseInt(verseAttr, 10) : NaN;
  if (Number.isNaN(verseNum)) return null;

  const pre = document.createRange();
  pre.selectNodeContents(body);
  pre.setEnd(range.startContainer, range.startOffset);
  const startOffset = pre.toString().length;
  const selectedLen = range.toString().length;
  const endOffset = startOffset + selectedLen;

  const maxLen = body.textContent?.length ?? 0;
  if (startOffset < 0 || endOffset > maxLen || endOffset <= startOffset) return null;

  return { verseNum, startOffset, endOffset, bodyEl: body };
}

/**
 * Intersect `range` with each [data-verse-body] under `rootEl` (document order).
 * Returns one segment per touched verse with offsets into that verse's body text.
 *
 * @param {Range} range
 * @param {HTMLElement} rootEl - e.g. homeReadingPassageRef container
 * @returns {{ verseNum: number, startOffset: number, endOffset: number }[]}
 */
export function getOffsetsForStudySelection(range, rootEl) {
  if (!range || range.collapsed || !rootEl?.querySelectorAll) return [];

  const bodies = rootEl.querySelectorAll("[data-verse-body]");
  /** @type {{ verseNum: number, startOffset: number, endOffset: number }[]} */
  const segments = [];

  for (const body of bodies) {
    if (!rangeIntersectsBody(range, body)) {
      continue;
    }

    const inner = document.createRange();
    inner.selectNodeContents(body);

    const sub = document.createRange();
    try {
      if (range.compareBoundaryPoints(Range.START_TO_START, inner) < 0) {
        sub.setStart(inner.startContainer, inner.startOffset);
      } else {
        sub.setStart(range.startContainer, range.startOffset);
      }
      if (range.compareBoundaryPoints(Range.END_TO_END, inner) > 0) {
        sub.setEnd(inner.endContainer, inner.endOffset);
      } else {
        sub.setEnd(range.endContainer, range.endOffset);
      }
    } catch {
      continue;
    }

    if (sub.collapsed) continue;

    const verseAttr = body.getAttribute("data-verse");
    const verseNum = verseAttr ? parseInt(verseAttr, 10) : NaN;
    if (Number.isNaN(verseNum)) continue;

    const pre = document.createRange();
    pre.selectNodeContents(body);
    pre.setEnd(sub.startContainer, sub.startOffset);
    const startOffset = pre.toString().length;
    const endOffset = startOffset + sub.toString().length;

    const maxLen = body.textContent?.length ?? 0;
    if (startOffset < 0 || endOffset > maxLen || endOffset <= startOffset) continue;

    segments.push({ verseNum, startOffset, endOffset });
  }

  return segments;
}
